/**
 * Blog Article Content Data
 * Full content for SEO/GEO optimized blog posts
 */

export const BLOG_CONTENT = {
  'yacht-crew-salary-guide': {
    slug: 'yacht-crew-salary-guide',
    title: 'Yacht Crew Salary Guide 2026 — What Every Position Pays',
    description: 'Complete yacht crew salary guide for 2026. Monthly pay ranges for deckhands, stewardesses, chefs, engineers, and captains by yacht size. Plus tips, rotations, and what affects your pay.',
    datePublished: '2026-03-26T00:00:00Z',
    dateModified: '2026-03-26T00:00:00Z',
    author: '1crew3 Team',
    keyFacts: [
      'Entry-level crew: €2,500–€3,500/month',
      'Mid-level crew (bosun, 2nd stew, 2nd engineer): €3,800–€7,000/month',
      'Senior crew (chief stew, head chef, chief engineer): €5,500–€15,000/month',
      'Captains: €8,000–€25,000+/month depending on yacht size',
      'Charter tips can add €1,000–€5,000+ per charter week',
      'Rotational positions typically pay 10–20% less base but include fixed time off'
    ],
    definitionLead: `Yacht crew salaries in 2026 range from €2,500 per month for entry-level deckhands on smaller yachts to over €25,000 per month for captains commanding superyachts above 100 metres. Pay varies primarily by position, yacht size, and whether the yacht is private or charter. Charter yachts typically offer lower base salaries but add significant tip income, while private yachts pay higher base rates with fewer or no tips. This guide covers every major position with current salary ranges based on industry data for 2026.`,
    sections: [
      {
        id: 'deck-department',
        title: 'Deck Department Salaries',
        content: `<p>The deck department is the most common entry point into yachting, with deckhand positions available on virtually every yacht. Salaries increase significantly with yacht size and the level of responsibility required.</p>

        <table class="w-full my-6 border-collapse">
          <thead>
            <tr class="bg-gray-100">
              <th class="border border-gray-300 px-4 py-2 text-left">Position</th>
              <th class="border border-gray-300 px-4 py-2 text-left">30–40m Yacht</th>
              <th class="border border-gray-300 px-4 py-2 text-left">50–70m Yacht</th>
              <th class="border border-gray-300 px-4 py-2 text-left">80m+ Yacht</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="border border-gray-300 px-4 py-2">Junior Deckhand</td>
              <td class="border border-gray-300 px-4 py-2">€2,500–€2,800</td>
              <td class="border border-gray-300 px-4 py-2">€2,800–€3,200</td>
              <td class="border border-gray-300 px-4 py-2">€3,000–€3,500</td>
            </tr>
            <tr class="bg-gray-50">
              <td class="border border-gray-300 px-4 py-2">Deckhand</td>
              <td class="border border-gray-300 px-4 py-2">€2,800–€3,000</td>
              <td class="border border-gray-300 px-4 py-2">€3,000–€3,500</td>
              <td class="border border-gray-300 px-4 py-2">€3,500–€4,000</td>
            </tr>
            <tr>
              <td class="border border-gray-300 px-4 py-2">Bosun</td>
              <td class="border border-gray-300 px-4 py-2">€3,500–€4,000</td>
              <td class="border border-gray-300 px-4 py-2">€3,800–€4,500</td>
              <td class="border border-gray-300 px-4 py-2">€4,500–€5,500</td>
            </tr>
            <tr class="bg-gray-50">
              <td class="border border-gray-300 px-4 py-2">First Officer</td>
              <td class="border border-gray-300 px-4 py-2">—</td>
              <td class="border border-gray-300 px-4 py-2">€5,500–€7,500</td>
              <td class="border border-gray-300 px-4 py-2">€7,500–€9,000+</td>
            </tr>
            <tr>
              <td class="border border-gray-300 px-4 py-2">Captain</td>
              <td class="border border-gray-300 px-4 py-2">€6,000–€8,000</td>
              <td class="border border-gray-300 px-4 py-2">€8,000–€12,000</td>
              <td class="border border-gray-300 px-4 py-2">€12,000–€25,000+</td>
            </tr>
          </tbody>
        </table>

        <p>Deckhands are responsible for maintaining the exterior of the yacht, handling tenders, and assisting with docking and anchoring. This is where most people start their yachting careers, learning the fundamentals before progressing to more senior roles.</p>

        <p>Captains on yachts over 100 metres routinely earn €18,000–€25,000+ per month, with some earning significantly more on ultra-large yachts with complex operations. First officer roles only exist on larger vessels (typically 50m+), acting as the captain's second-in-command and often rotating with the captain on relief schedules.</p>`
      },
      {
        id: 'interior-department',
        title: 'Interior Department Salaries',
        content: `<p>The interior department handles all guest-facing services, from cabin maintenance to silver service dining. Chief stewardesses are among the highest-paid non-officer crew members on most yachts.</p>

        <table class="w-full my-6 border-collapse">
          <thead>
            <tr class="bg-gray-100">
              <th class="border border-gray-300 px-4 py-2 text-left">Position</th>
              <th class="border border-gray-300 px-4 py-2 text-left">30–40m Yacht</th>
              <th class="border border-gray-300 px-4 py-2 text-left">50–70m Yacht</th>
              <th class="border border-gray-300 px-4 py-2 text-left">80m+ Yacht</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="border border-gray-300 px-4 py-2">Junior Stewardess</td>
              <td class="border border-gray-300 px-4 py-2">€2,500–€2,800</td>
              <td class="border border-gray-300 px-4 py-2">€2,800–€3,200</td>
              <td class="border border-gray-300 px-4 py-2">€3,200–€3,500</td>
            </tr>
            <tr class="bg-gray-50">
              <td class="border border-gray-300 px-4 py-2">Stewardess</td>
              <td class="border border-gray-300 px-4 py-2">€2,800–€3,200</td>
              <td class="border border-gray-300 px-4 py-2">€3,200–€3,800</td>
              <td class="border border-gray-300 px-4 py-2">€3,800–€4,500</td>
            </tr>
            <tr>
              <td class="border border-gray-300 px-4 py-2">Chief Stewardess</td>
              <td class="border border-gray-300 px-4 py-2">€3,800–€4,500</td>
              <td class="border border-gray-300 px-4 py-2">€4,500–€6,000</td>
              <td class="border border-gray-300 px-4 py-2">€6,000–€8,000+</td>
            </tr>
            <tr class="bg-gray-50">
              <td class="border border-gray-300 px-4 py-2">Purser</td>
              <td class="border border-gray-300 px-4 py-2">—</td>
              <td class="border border-gray-300 px-4 py-2">€4,500–€6,000</td>
              <td class="border border-gray-300 px-4 py-2">€6,000–€8,000</td>
            </tr>
          </tbody>
        </table>

        <p>The chief stewardess role is one of the highest-paying non-officer positions on most yachts. They manage the entire interior team, coordinate with the chef on guest services, and ensure the yacht maintains impeccable standards at all times.</p>

        <p>Pursers handle the administrative and financial side of yacht operations—managing accounts, provisioning, crew documentation, and often acting as HR for the interior department. On charter yachts, interior crew often receive the largest share of tips due to their direct guest interaction.</p>`
      },
      {
        id: 'galley-department',
        title: 'Galley (Chef) Salaries',
        content: `<p>Yacht chefs are among the highest-paid crew members relative to team size. The demanding nature of the role—cooking for discerning guests with diverse preferences while managing provisioning in remote locations—commands premium compensation.</p>

        <table class="w-full my-6 border-collapse">
          <thead>
            <tr class="bg-gray-100">
              <th class="border border-gray-300 px-4 py-2 text-left">Position</th>
              <th class="border border-gray-300 px-4 py-2 text-left">30–40m Yacht</th>
              <th class="border border-gray-300 px-4 py-2 text-left">50–70m Yacht</th>
              <th class="border border-gray-300 px-4 py-2 text-left">80m+ Yacht</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="border border-gray-300 px-4 py-2">Sole Chef / Cook</td>
              <td class="border border-gray-300 px-4 py-2">€3,500–€5,000</td>
              <td class="border border-gray-300 px-4 py-2">€4,500–€7,000</td>
              <td class="border border-gray-300 px-4 py-2">—</td>
            </tr>
            <tr class="bg-gray-50">
              <td class="border border-gray-300 px-4 py-2">Head Chef</td>
              <td class="border border-gray-300 px-4 py-2">—</td>
              <td class="border border-gray-300 px-4 py-2">€7,000–€9,000</td>
              <td class="border border-gray-300 px-4 py-2">€9,000–€12,000+</td>
            </tr>
            <tr>
              <td class="border border-gray-300 px-4 py-2">Sous Chef</td>
              <td class="border border-gray-300 px-4 py-2">—</td>
              <td class="border border-gray-300 px-4 py-2">€3,500–€4,500</td>
              <td class="border border-gray-300 px-4 py-2">€4,500–€6,000</td>
            </tr>
          </tbody>
        </table>

        <p>Smaller yachts (30-50m) typically employ a sole chef who handles everything from crew meals to elaborate guest dinners. This role requires versatility, creativity, and the ability to work independently under pressure.</p>

        <p>Michelin-trained chefs on large yachts can command premium salaries of €10,000–€15,000+ per month, especially if they have a proven track record with high-profile charter guests or owners. The best yacht chefs are in constant demand and can essentially choose their positions.</p>`
      },
      {
        id: 'engineering-department',
        title: 'Engineering Department Salaries',
        content: `<p>Engineering remains one of the highest-demand departments in yachting. As yachts become more technologically complex, skilled engineers command increasingly competitive salaries.</p>

        <table class="w-full my-6 border-collapse">
          <thead>
            <tr class="bg-gray-100">
              <th class="border border-gray-300 px-4 py-2 text-left">Position</th>
              <th class="border border-gray-300 px-4 py-2 text-left">30–40m Yacht</th>
              <th class="border border-gray-300 px-4 py-2 text-left">50–70m Yacht</th>
              <th class="border border-gray-300 px-4 py-2 text-left">80m+ Yacht</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="border border-gray-300 px-4 py-2">Junior Engineer</td>
              <td class="border border-gray-300 px-4 py-2">€3,000–€3,500</td>
              <td class="border border-gray-300 px-4 py-2">€3,500–€4,500</td>
              <td class="border border-gray-300 px-4 py-2">€4,500–€5,500</td>
            </tr>
            <tr class="bg-gray-50">
              <td class="border border-gray-300 px-4 py-2">Second Engineer</td>
              <td class="border border-gray-300 px-4 py-2">—</td>
              <td class="border border-gray-300 px-4 py-2">€5,000–€7,000</td>
              <td class="border border-gray-300 px-4 py-2">€6,000–€8,000</td>
            </tr>
            <tr>
              <td class="border border-gray-300 px-4 py-2">Chief Engineer</td>
              <td class="border border-gray-300 px-4 py-2">€4,500–€6,000</td>
              <td class="border border-gray-300 px-4 py-2">€7,000–€10,000</td>
              <td class="border border-gray-300 px-4 py-2">€10,000–€15,000+</td>
            </tr>
            <tr class="bg-gray-50">
              <td class="border border-gray-300 px-4 py-2">ETO</td>
              <td class="border border-gray-300 px-4 py-2">—</td>
              <td class="border border-gray-300 px-4 py-2">€5,000–€7,000</td>
              <td class="border border-gray-300 px-4 py-2">€7,000–€9,000</td>
            </tr>
          </tbody>
        </table>

        <p>Chief engineers on large yachts manage complex systems including main engines, generators, HVAC, watermakers, and increasingly sophisticated automation systems. Their salaries reflect the critical nature of their role and the specialized qualifications required.</p>

        <p>ETOs (Electro-Technical Officers) are increasingly sought after as yachts become more technologically complex. They handle AV systems, IT infrastructure, navigation electronics, and automation—skills that are in high demand and short supply in the industry.</p>`
      },
      {
        id: 'salary-factors',
        title: 'What Affects Your Salary',
        content: `<h3 class="text-xl font-semibold text-gray-800 mt-6 mb-3">Yacht Size</h3>
        <p>Yacht size is the single biggest factor determining salary. A deckhand on a 30m yacht might earn €2,800/month, while the same role on an 80m+ superyacht pays €3,500–€4,000/month. This premium reflects the increased complexity, larger crew, and higher expectations on larger vessels.</p>

        <h3 class="text-xl font-semibold text-gray-800 mt-6 mb-3">Private vs. Charter</h3>
        <p>Private yachts typically pay higher base salaries but offer little to no tip income. Charter yachts pay lower base rates but add significant tip potential—a busy charter season can add €10,000–€30,000+ to your annual earnings. Your preference depends on whether you value income stability or earning potential.</p>

        <h3 class="text-xl font-semibold text-gray-800 mt-6 mb-3">Rotational vs. Permanent</h3>
        <p>Rotational positions (e.g., 2 months on, 2 months off) typically pay 10–20% less in base salary but offer predictable time off for work-life balance. Permanent positions pay more but may have limited leave, though some permanent roles on private yachts offer generous vacation packages.</p>

        <h3 class="text-xl font-semibold text-gray-800 mt-6 mb-3">Experience and Certifications</h3>
        <p>Additional qualifications directly impact earning potential. Yachtmaster certifications for deck crew, Y4 engineering tickets, advanced wine certifications for stews—all command salary premiums. Experience on larger or more prestigious yachts also increases your market value.</p>

        <h3 class="text-xl font-semibold text-gray-800 mt-6 mb-3">Season and Demand</h3>
        <p>Peak hiring seasons (March–May for Mediterranean, October–November for Caribbean) can allow experienced crew to negotiate premium rates as yacht owners rush to fill positions. Conversely, off-season job seekers may have less leverage.</p>

        <h3 class="text-xl font-semibold text-gray-800 mt-6 mb-3">Location</h3>
        <p>Mediterranean and Middle East positions tend to pay slightly higher than Caribbean roles, though the difference is often offset by tip potential on busy Caribbean charter yachts. Some regions (e.g., Middle East) also offer tax advantages.</p>`
      },
      {
        id: 'charter-tips',
        title: 'Understanding Charter Tips',
        content: `<p>On charter yachts, tips represent a significant portion of crew income. The standard charter tip ranges from 5% to 15% of the charter fee, with 10–15% being common for excellent service. On a €200,000 weekly charter with a 10% tip, that's €20,000 split among the crew.</p>

        <p>Tips are typically distributed based on position and tenure. Captains usually receive a larger share (often 10–15% of the tip pool), with the remainder divided among crew. Some yachts use equal splits, while others weight by position. Interior crew often receive favorable splits due to their direct guest interaction.</p>

        <p>A busy charter season with back-to-back charters can add €10,000–€30,000+ to your annual income. However, tips are not guaranteed—they depend on charter bookings, guest satisfaction, and generosity. Private yacht crew rarely receive tips, which is why their base salaries are typically higher.</p>`
      }
    ],
    faqs: [
      {
        question: 'How much does a yacht deckhand earn per month?',
        answer: 'A yacht deckhand earns between €2,800 and €4,000 per month in 2026, depending on yacht size and experience. Entry-level junior deckhands start around €2,500/month on smaller yachts.'
      },
      {
        question: 'Do yacht crew get free accommodation and food?',
        answer: 'Yes. Yacht crew live on board and all meals are provided by the yacht chef. This means your entire salary is essentially disposable income, with no rent, food, or utility costs.'
      },
      {
        question: 'How much do yacht crew make in tips?',
        answer: 'Tips on charter yachts typically range from €1,000 to €5,000+ per charter week, split among the crew. A busy charter season can add €10,000 to €30,000+ to your annual income. Private yacht crew generally do not receive regular tips.'
      },
      {
        question: 'What is the highest paying yacht crew position?',
        answer: 'Captain is the highest-paid position, earning €12,000 to €25,000+ per month on large superyachts. Among non-officer roles, head chefs and chief engineers are the highest paid at €7,000 to €15,000/month.'
      },
      {
        question: 'Are yacht crew salaries tax-free?',
        answer: "It depends on your nationality and the yacht's flag state. Many yacht crew qualify for tax advantages due to working internationally, but tax obligations vary. Crew should consult a maritime tax specialist. 1crew3 does not provide tax advice."
      },
      {
        question: 'Do yacht crew salaries include benefits?',
        answer: 'Most yacht positions include accommodation, all meals, health insurance, and travel expenses in addition to the base salary. Some yachts also offer pension contributions, training allowances, and annual flight tickets home.'
      }
    ],
    ctaTitle: 'Ready to find your next position?',
    ctaText: 'Browse open yacht crew jobs on 1crew3 or create your free crew profile to get discovered by captains hiring now.'
  },

  'how-to-become-yacht-crew': {
    slug: 'how-to-become-yacht-crew',
    title: 'How to Become Yacht Crew — Complete Beginner\'s Guide (2026)',
    description: 'Step-by-step guide to becoming yacht crew with no experience. STCW training, ENG1 medical, costs, timeline, and how to land your first yacht job in 2026.',
    datePublished: '2026-03-26T00:00:00Z',
    dateModified: '2026-03-26T00:00:00Z',
    author: '1crew3 Team',
    keyFacts: [
      'Minimum requirements: ENG1 medical + STCW Basic Safety Training',
      'Training cost: €1,500–€4,000 (basic to comprehensive course)',
      'Timeline: 1–3 months from training to first job',
      'No prior experience needed for entry-level roles',
      'Peak hiring: March–May (Med season) and October–November (Caribbean season)',
      'Entry-level salary: €2,500–€3,500/month with free accommodation and food'
    ],
    definitionLead: `Becoming yacht crew requires three things: an ENG1 seafarer's medical certificate, STCW Basic Safety Training, and the determination to find your first position. The entire process from zero experience to landing your first job typically takes 1 to 3 months and costs between €1,500 and €4,000 depending on the training path you choose. No prior maritime experience is needed for entry-level positions like deckhand or junior stewardess — thousands of people enter the yachting industry every year from completely unrelated backgrounds.`,
    sections: [
      {
        id: 'step-1-eng1',
        title: 'Step 1 — Get Your ENG1 Medical Certificate',
        content: `<p>The ENG1 is a seafarer's medical certificate issued by MCA-approved doctors. It confirms you're medically fit to work at sea and is the first document you should obtain before spending money on any training courses.</p>

        <p>Get your ENG1 FIRST. There's no point completing an STCW course if you subsequently fail the medical examination. The ENG1 assessment takes approximately 30-40 minutes and includes checks of your vision, hearing, mobility, and general health. It's non-invasive and most people pass without issues.</p>

        <p>The examination costs approximately £100-£150 in the UK, with similar prices at MCA-approved doctors in France, Spain, USA, and other major yachting locations. If you wear glasses or have a pre-existing condition, you can still pass—the criteria are reasonable, and doctors will advise on any restrictions. The ENG1 is valid for 2 years (1 year if you're over 65).</p>`
      },
      {
        id: 'step-2-stcw',
        title: 'Step 2 — Complete STCW Basic Safety Training',
        content: `<p>STCW (Standards of Training, Certification and Watchkeeping) is the internationally recognized safety certification required for all professional seafarers. Without it, you cannot legally work on a commercially registered yacht.</p>

        <p>The STCW Basic Safety Training course takes 5 consecutive days and covers four modules: Personal Survival Techniques (sea survival), Fire Prevention and Fire Fighting, Personal Safety and Social Responsibilities, and Elementary First Aid. You'll learn practical skills like using life rafts, fighting fires with extinguishers and hoses, and administering first aid in maritime environments.</p>

        <p>Course costs range from €800–€1,500 depending on location. Popular training centres include those in Antibes (France), Palma de Mallorca (Spain), Fort Lauderdale (USA), Southampton and Portsmouth (UK), and Auckland (New Zealand). The STCW Basic Safety Training certificate is valid for 5 years, after which you'll need a refresher course (typically 1-2 days) to maintain certification.</p>`
      },
      {
        id: 'step-3-professional-course',
        title: 'Step 3 — Consider a Professional Training Course (Recommended)',
        content: `<p>While ENG1 + STCW is technically all you need to start applying for positions, a professional deckhand or stewardess course dramatically increases your chances of landing a job—especially your first one.</p>

        <p>These courses typically last 3-4 weeks and cost €2,000-€4,000. They teach you practical yacht-specific skills that employers actually care about: line handling and docking procedures, tender driving and maintenance, silver service and wine service, yacht housekeeping standards, laundry care for luxury fabrics, and more. They also provide extra qualifications like Powerboat Level 2, VHF radio licence, and food hygiene certificates.</p>

        <p>Crucially, training schools provide industry contacts and networking opportunities that can lead directly to job offers. Schools like Flying Fish, UKSA, Bluewater, and the Crew Academy all have placement assistance programs and connections with yacht management companies. Many students receive their first job offer through their training school's network.</p>`
      },
      {
        id: 'step-4-cv',
        title: 'Step 4 — Build Your Yacht Crew CV',
        content: `<p>A yacht crew CV is fundamentally different from a regular CV. It should be exactly one page, include a professional headshot, list your certifications prominently, and highlight any hospitality or relevant transferable skills.</p>

        <p>Your CV should include: a clear, professional headshot (properly cropped, neutral background, appropriate attire); personal details including nationality and visa status; all certifications with expiry dates; relevant experience (even non-yacht experience in hospitality, sports, or trades); languages spoken; and your availability and preferred position.</p>

        <p>Keep the design clean, simple, and professional—yacht captains review dozens of CVs and appreciate clarity over creativity. Registration on platforms like <a href="/register" class="text-blue-600 hover:text-blue-800">1crew3</a> lets you create a digital profile that captains can search and find directly, complementing your traditional CV. For detailed guidance, see our <a href="/blog/yacht-crew-cv-template" class="text-blue-600 hover:text-blue-800">Yacht Crew CV Template</a> guide.</p>`
      },
      {
        id: 'step-5-find-job',
        title: 'Step 5 — Find Your First Job',
        content: `<p><strong>Online platforms</strong> — Register on <a href="/register" class="text-blue-600 hover:text-blue-800">1crew3</a> and similar platforms, set up job alerts for positions matching your qualifications, and apply promptly to new listings. Many positions are filled within days of posting, so speed matters. Complete your profile thoroughly—captains often search for crew rather than posting positions.</p>

        <p><strong>Dockwalking</strong> — Physically walking the marinas in yachting hubs (Antibes, Fort Lauderdale, Palma) and handing your CV to crew on yachts remains one of the most effective methods for first-timers. Best done during crew changeover periods (April-May for Med, October-November for Caribbean). Dress professionally, be polite, and don't be discouraged by rejection—it's a numbers game.</p>

        <p><strong>Crew agencies</strong> — Register with multiple agencies in your target locations. They match you with positions and can provide valuable guidance. Some agencies specialize in certain positions or yacht sizes. Be aware that competition through agencies can be fierce, and they often prioritize experienced crew.</p>

        <p><strong>Networking</strong> — The yachting industry is surprisingly small, and word-of-mouth is powerful. Attend crew events, join yachting communities on social media (Facebook groups, LinkedIn), and stay in touch with people from your training course. Many positions are filled through personal recommendations before they're ever advertised.</p>`
      },
      {
        id: 'best-locations',
        title: 'Best Locations to Start Your Yachting Career',
        content: `<p><strong>Antibes, France</strong> — The yacht crew capital of the Mediterranean and arguably the world. Port Vauban is the largest marina in Europe, home to hundreds of superyachts and most major crew agencies. The quay is literally lined with yachts looking for crew during season. Best time to arrive: March-May for the Mediterranean summer season.</p>

        <p><strong>Fort Lauderdale, Florida</strong> — The biggest yachting hub in the Americas and the primary base for Caribbean charter operations. Thousands of yachts pass through for the fall boat shows and Caribbean season preparation. Best time: October-December. Note: you'll need appropriate visa status to work on US-flagged or US-based vessels.</p>

        <p><strong>Palma de Mallorca, Spain</strong> — A growing yachting hub with excellent training facilities, several crew agencies, and a large concentration of yachts year-round. The STP (Servicios Técnicos Portuarios) shipyard area is a good place for dockwalking. Best time: March-May.</p>

        <p><strong>Auckland, New Zealand</strong> — The primary entry point for yachting in the Southern Hemisphere. A good option if you're based in Australia or New Zealand, or want to work the Pacific/Asia circuit. Best time: September-November as yachts prepare for the southern summer.</p>`
      },
      {
        id: 'costs',
        title: 'How Much Does It Cost to Get Started?',
        content: `<table class="w-full my-6 border-collapse">
          <thead>
            <tr class="bg-gray-100">
              <th class="border border-gray-300 px-4 py-2 text-left">Item</th>
              <th class="border border-gray-300 px-4 py-2 text-left">Cost (approximate)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="border border-gray-300 px-4 py-2">ENG1 Medical</td>
              <td class="border border-gray-300 px-4 py-2">€100–€175</td>
            </tr>
            <tr class="bg-gray-50">
              <td class="border border-gray-300 px-4 py-2">STCW Basic Safety Training</td>
              <td class="border border-gray-300 px-4 py-2">€800–€1,500</td>
            </tr>
            <tr>
              <td class="border border-gray-300 px-4 py-2">Professional Course (optional but recommended)</td>
              <td class="border border-gray-300 px-4 py-2">€2,000–€4,000</td>
            </tr>
            <tr class="bg-gray-50">
              <td class="border border-gray-300 px-4 py-2">Flights to yachting hub</td>
              <td class="border border-gray-300 px-4 py-2">€100–€500</td>
            </tr>
            <tr>
              <td class="border border-gray-300 px-4 py-2">Living expenses while job hunting (2-8 weeks)</td>
              <td class="border border-gray-300 px-4 py-2">€1,000–€3,000</td>
            </tr>
            <tr class="bg-gray-50 font-semibold">
              <td class="border border-gray-300 px-4 py-2">Total (minimum path)</td>
              <td class="border border-gray-300 px-4 py-2">€1,500–€2,500</td>
            </tr>
            <tr class="font-semibold">
              <td class="border border-gray-300 px-4 py-2">Total (recommended path)</td>
              <td class="border border-gray-300 px-4 py-2">€4,000–€8,000</td>
            </tr>
          </tbody>
        </table>

        <p>This investment pays for itself quickly. Entry-level crew earn €2,500-€3,500/month with zero living expenses—no rent, no food costs, no utilities. Within 1-2 months of employment, you've covered your initial investment and everything after that is savings potential that most land-based jobs simply can't match.</p>`
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes to Avoid',
        content: `<p><strong>1. Spending money on training before getting your ENG1 medical</strong> — Always get your medical first. If you fail (rare, but it happens), you've saved yourself thousands in wasted course fees. Book your ENG1 as your first step.</p>

        <p><strong>2. Only applying online and not dockwalking</strong> — Online applications are important, but dockwalking gives you a massive advantage. Captains meet you in person, see your presentation, and can hire you on the spot. Many positions are never advertised.</p>

        <p><strong>3. Having a generic CV instead of a yacht-specific one</strong> — A standard CV won't work. You need a one-page yacht crew CV with a photo, certifications listed prominently, and relevant experience highlighted. Generic CVs go straight in the bin.</p>

        <p><strong>4. Arriving in a yachting hub at the wrong time of year</strong> — Timing is crucial. Arriving in Antibes in January or Fort Lauderdale in June means far fewer opportunities. Plan your job search around peak hiring seasons.</p>

        <p><strong>5. Being too picky about your first position</strong> — Your first job is about getting experience, not finding the perfect yacht. Take opportunities on smaller boats, do delivery trips, accept temporary positions. Get your sea time, prove yourself, and better opportunities will follow.</p>

        <p><strong>6. Not having enough savings to sustain yourself during the job search</strong> — The job search can take 2-8 weeks or longer. Running out of money and having to go home is demoralizing and expensive. Budget for at least 6-8 weeks of living expenses.</p>`
      },
      {
        id: 'timeline',
        title: 'Timeline — From Zero to Your First Yacht Job',
        content: `<table class="w-full my-6 border-collapse">
          <thead>
            <tr class="bg-gray-100">
              <th class="border border-gray-300 px-4 py-2 text-left">Week</th>
              <th class="border border-gray-300 px-4 py-2 text-left">Activity</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="border border-gray-300 px-4 py-2">Week 1</td>
              <td class="border border-gray-300 px-4 py-2">Book and complete ENG1 medical</td>
            </tr>
            <tr class="bg-gray-50">
              <td class="border border-gray-300 px-4 py-2">Weeks 2–3</td>
              <td class="border border-gray-300 px-4 py-2">STCW Basic Safety Training (5 days)</td>
            </tr>
            <tr>
              <td class="border border-gray-300 px-4 py-2">Weeks 3–6</td>
              <td class="border border-gray-300 px-4 py-2">Professional deckhand or stewardess course (optional, 3-4 weeks)</td>
            </tr>
            <tr class="bg-gray-50">
              <td class="border border-gray-300 px-4 py-2">Weeks 4–7</td>
              <td class="border border-gray-300 px-4 py-2">Register on 1crew3, build yacht CV, apply online, register with agencies</td>
            </tr>
            <tr>
              <td class="border border-gray-300 px-4 py-2">Weeks 5–12</td>
              <td class="border border-gray-300 px-4 py-2">Dockwalking, interviewing, networking</td>
            </tr>
            <tr class="bg-gray-50">
              <td class="border border-gray-300 px-4 py-2">Weeks 6–12</td>
              <td class="border border-gray-300 px-4 py-2">Land your first position</td>
            </tr>
          </tbody>
        </table>`
      }
    ],
    faqs: [
      {
        question: 'Can I become yacht crew with no experience?',
        answer: "Yes. Entry-level positions like deckhand and junior stewardess are designed for people with no prior maritime experience. You need an ENG1 medical and STCW Basic Safety Training, which takes about one week. Thousands of people enter the industry each year from backgrounds in hospitality, fitness, trades, and other fields."
      },
      {
        question: 'How long does it take to become yacht crew?',
        answer: 'From starting training to landing your first job typically takes 1 to 3 months. The ENG1 medical takes one day, STCW training takes 5 days, and the job search takes 2 to 8 weeks depending on the season and your approach.'
      },
      {
        question: 'How much does it cost to become yacht crew?',
        answer: 'The minimum path (ENG1 + STCW only) costs €1,500 to €2,500. The recommended path including a professional training course costs €4,000 to €8,000. This investment pays for itself within 1-2 months of employment since crew live and eat on board for free.'
      },
      {
        question: 'What qualifications do I need to work on a yacht?',
        answer: "At minimum, you need an ENG1 seafarer's medical certificate and STCW Basic Safety Training. Additional qualifications like Powerboat Level 2, VHF radio licence, and food hygiene certificates improve your chances. Specialized roles like engineer or captain require specific maritime licences."
      },
      {
        question: 'What is the best time of year to look for yacht crew jobs?',
        answer: 'The two peak hiring seasons are March to May (yachts preparing for the Mediterranean summer season) and October to November (preparing for the Caribbean winter season). However, positions are available year-round.'
      },
      {
        question: 'Do I need to speak multiple languages to work on a yacht?',
        answer: 'English is the working language on almost all international yachts, so fluent English is essential. Additional languages (especially French, Spanish, or Italian) are a bonus but not required for most entry-level positions.'
      }
    ],
    ctaTitle: 'Ready to start your yachting career?',
    ctaText: 'Create your free crew profile on 1crew3 and start browsing open positions today.'
  }
};

/**
 * Get full blog content by slug
 */
export const getBlogContent = (slug) => {
  return BLOG_CONTENT[slug] || null;
};

/**
 * Check if full content exists for a slug
 */
export const hasFullContent = (slug) => {
  return !!BLOG_CONTENT[slug];
};
